import { useEffect, useState } from 'react';

/** 单条模板字段的最小形态：只要 setUniqueMapByVariables 实际读到的字段 */

/** flowExcelStore 的结构形态。导出供 formInit 等外部模块引用。 */

/**
 * formInit 函数签名：由 /js/formInit.js 运行时拉取并注入。
 * 放在 FlowExcelStoreShape 后面声明以避免循环引用。
 */

const store = {
  form: null,
  approveForm: null,
  uniqueMap: new Map(),
  currentFieldsMap: new Map(),
  approveUsers: [],
  // 函数声明会被提升，可在字面量里直接引用（见下方 setApproveUsersRaw 等）
  setApproveUsers: setApproveUsersRaw,
  removeApproveUsersByValue,
  getOriginalApproveUsers,
  formEvent: undefined,
  runtimeFormInit: undefined
};

// ----- approveUsers 外置订阅器：模块级 state + 订阅者集合 -----
const approveUsersListeners = new Set();

// ----- formEvent 外置订阅器：当前 formCode 对应的业务回调 -----
const formEventListeners = new Set();

// 原始未裁剪的 approveUsers（用于双向恢复）。
// 由 initApproveUsers 显式写入，clearAll 清空。
let originalApproveUsers = [];

/** 读取原始快照 */
export function getOriginalApproveUsers() {
  return originalApproveUsers;
}

/** 显式初始化：覆盖当前值 + 刷新快照。FlowCenterModal 拉枚举后调用。 */
export function initApproveUsers(users) {
  originalApproveUsers = users.map(n => ({
    ...n
  }));
  store.approveUsers = users.map(n => ({
    ...n
  }));
  approveUsersListeners.forEach(cb => cb(store.approveUsers));
}
function setApproveUsersRaw(users) {
  const next = typeof users === 'function' ? users(store.approveUsers) : users;
  store.approveUsers = next;
  approveUsersListeners.forEach(cb => cb(next));
}

/** 订阅 approveUsers。返回当前值 + setter，组件可通过 setter 修改数组。 */
export function useApproveUsers() {
  const [users, setUsers] = useState(store.approveUsers);
  useEffect(() => {
    approveUsersListeners.add(setUsers);
    // 订阅时拉一次最新值
    setUsers(store.approveUsers);
    return () => {
      approveUsersListeners.delete(setUsers);
    };
  }, []);
  return [users, setApproveUsersRaw];
}

/** 任意位置读 / 写 approveUsers（无 hook 语义） */
export function getApproveUsers() {
  return store.approveUsers;
}
export const setApproveUsers = setApproveUsersRaw;

// ----- formEvent：当前 formCode 对应的业务回调（init/onSubmitBefore/...） -----

// ----- runtimeFormInit：loadRuntimeConfig 注入的 formInit 函数 -----

/**
 * 设置运行时 formInit。由 loadRuntimeConfig 拉 /js/formInit.js 后调用。
 */
export function setRuntimeFormInit(fn) {
  store.runtimeFormInit = fn;
}

/** 任意位置读取运行时 formInit（非 hook） */
export function getRuntimeFormInit() {
  return store.runtimeFormInit;
}

/**
 * 设置当前 formCode 对应的业务回调。
 * FlowCenterModal 在 useEffect [formCode] 拉 formInit() 后调用，
 * 让其它子组件（ExcelFormBody / ApproveForm 等）能跨组件订阅到。
 */
export function setFormEvent(event) {
  store.formEvent = event;
  formEventListeners.forEach(cb => cb(event));
}

/** 任意位置读取当前 formCode 的业务回调（非 hook） */
export function getFormEvent() {
  return store.formEvent;
}

/** 订阅 formEvent 变更；返回当前值 + setter */
export function useFormEvent() {
  const [event, setLocal] = useState(store.formEvent);
  useEffect(() => {
    formEventListeners.add(setLocal);
    setLocal(store.formEvent);
    return () => {
      formEventListeners.delete(setLocal);
    };
  }, []);
  return [event, setFormEvent];
}

/**
 * 按 node.value 剔除指定节点（支持多个 value）。
 *
 * - 第二个参数可以是字符串数组 / Set<string> / 单个字符串。
 * - 不修改原始快照。
 */
export function removeApproveUsersByValue(values) {
  const blocked = typeof values === 'string' ? new Set([values]) : values instanceof Set ? values : new Set(values);
  setApproveUsers(prev => prev.filter(n => !blocked.has(n.value)));
}

/**
 * pickFn / pickValues 的并集形态：
 *  - function：返回 true 表示保留
 *  - string[] / Set<string>：按 node.value 剔除命中项
 */

function normalizePickRule(rule) {
  if (typeof rule === 'function') return rule;
  if (rule === undefined) return () => true;
  if (typeof rule === 'string') {
    return node => node.value !== rule;
  }
  const blocked = rule instanceof Set ? rule : new Set(rule);
  return node => !blocked.has(node.value);
}

/**
 * 根据外部条件动态过滤 approveUsers，并在条件不再满足时按需恢复。
 *
 * - `filter`：返回 true 时表示"进入过滤态"——会用 `pick` 把要剔除的节点过滤掉。
 * - `restore`：返回 true 时表示"恢复原始快照"。
 * - `pick`：第三参可以是
 *     1. `(node) => boolean` —— 返回 true 保留
 *     2. `string[]` / `Set<string>` —— 命中 node.value 的节点被剔除
 *
 * 简化：
 *   // 单个 value
 *   useFilterApproveUsers(
 *     () => leaveDays <= 3,
 *     () => leaveDays > 3,
 *     ['gmApprove']
 *   );
 *
 *   // 多个 value
 *   useFilterApproveUsers(
 *     () => leaveDays <= 3,
 *     () => leaveDays > 3,
 *     ['gmApprove', 'hrApprove']
 *   );
 *
 *   // 自定义函数（带条件）
 *   useFilterApproveUsers(
 *     () => leaveDays <= 3,
 *     () => leaveDays > 3,
 *     (node) => !['gmApprove', 'hrApprove'].includes(node.value)
 *   );
 */
export function useFilterApproveUsers(filter, restore, pick = () => true,
/**
 * 过滤基于"原始快照"还是"当前 store 值"。
 * - true（默认）：每次都从原始快照重新过滤。多 hook 串联时**会互相覆盖**，
 *   只适合单独使用。
 * - false：基于当前数组再过滤。多 hook 串联（每个剔除不同 value）时
 *   需要选这个。
 */
baseOnSnapshot = true) {
  useEffect(() => {
    const snapshot = originalApproveUsers;
    if (snapshot.length === 0) return;
    const pickFn = normalizePickRule(pick);
    // 把"我管的值集合"从 pick 推出来，给 restore 用
    const blockedValues = inferBlockedValues(pick);
    if (filter(snapshot)) {
      const source = baseOnSnapshot ? snapshot : store.approveUsers;
      setApproveUsers(source.filter(pickFn));
    } else if (restore(snapshot)) {
      const current = store.approveUsers;
      // 仅当自己管的值在 snapshot 里存在、但 current 里不存在时才补回；
      // 不影响其他 hook 删的节点。
      const missing = blockedValues.filter(v => snapshot.some(n => n.value === v) && !current.some(n => n.value === v));
      if (missing.length > 0) {
        const merged = [...current];
        missing.forEach(v => {
          const original = snapshot.find(n => n.value === v);
          if (original) merged.push(original);
        });
        setApproveUsers(merged);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, restore, pick, baseOnSnapshot]);
}

/** 从 pick 规则反推出"我剔除的 value 集合"，给 restore 用来精确补回 */
function inferBlockedValues(pick) {
  if (typeof pick === 'string') return [pick];
  if (Array.isArray(pick)) return [...pick];
  if (pick instanceof Set) return Array.from(pick);
  return [];
}

/**
 * 把 form 实例写入全局，unmount 时自动清空。
 *
 * ⚠️ 注意：React 18 StrictMode 下 effect 会 mount → unmount → remount 跑两遍，
 * 跨组件 unmount/mount 的中间空窗期内，外部读 store.form 可能拿到 null。
 * 调用方要么接受这个语义，要么改成 ref + 后注册覆盖（不要 cleanup），
 * 但那会引入内存泄漏。当前语义是 "最后挂载的表单实例为准"，够用。
 */
export function useFlowForm(form) {
  useEffect(() => {
    store.form = form;
    return () => {
      // 只在「这个 effect 写入的实例」还在线上时才清，避免被新实例误清
      if (store.form === form) {
        store.form = null;
      }
    };
  }, [form]);
}

/** 同 useFlowForm，仅多一层保护 */
export function useApproveForm(form) {
  useEffect(() => {
    store.approveForm = form;
    return () => {
      if (store.approveForm === form) {
        store.approveForm = null;
      }
    };
  }, [form]);
}

/** 直接读写（同模块内才需要，外部用上面两个 hook） */
export function getFlowForm() {
  return store.form;
}
export function getApproveForm() {
  return store.approveForm;
}

/**
 * 设置唯一映射：用 `${entityType}.${name}` 或 `name` 作 key。
 *
 * - 清空旧映射再写入新映射（外部语义上每次调用都是"整批替换"）。
 * - 跳过 name 缺失的字段，避免把 key 写成 'undefined' 撞键。
 * - callback 每条都触发一次（用于外部副作用，例如埋点）。
 */
export function setUniqueMapByVariables(variables, callback) {
  store.uniqueMap.clear();
  variables?.forEach?.(field => {
    callback?.(field);
    const name = field.name;
    if (name == null || name === '') return; // name 缺失 → 跳过
    const entityType = (field.entityType ?? '').trim();
    const key = entityType ? `${entityType}.${name}` : name;
    store.uniqueMap.set(key, field);
  });
}

/**
 * 设置当前字段映射：用 `${type}.${variable}` 或 `variable` 作 key。
 *
 * - 清空旧映射再写入新映射（外部语义上每次调用都是"整批替换"）。
 * - 跳过 variable 缺失的字段，避免把 key 写成 'undefined' 撞键。
 * - callback 每条都触发一次（用于外部副作用，例如埋点）。
 */
export function setCurrentFieldsMap(variables, callback) {
  const map = new Map();
  variables.forEach(field => {
    const variable = field.variable;
    if (variable == null || variable === '') return; // variable 缺失 → 跳过
    const type = (field.type ?? '').trim();
    const key = type ? `${type}.${variable}` : variable;
    callback?.(field);
    map.set(key, field);
  });
  store.currentFieldsMap = map;
}

/** 清空当前字段映射 */
export function clearCurrentFieldsMap() {
  store.currentFieldsMap.clear();
}
export function getCurrentFieldsMap() {
  return store.currentFieldsMap;
}
export function getUniqueMap() {
  return store.uniqueMap;
}

/** 清空唯一映射 */
export function clearUniqueMap() {
  store.uniqueMap.clear();
}
export function clearAll() {
  clearCurrentFieldsMap();
  clearUniqueMap();
  setApproveUsersRaw([]);
  originalApproveUsers = [];
  setFormEvent(undefined);
}
export { store as flowExcelStore };